import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Landmark, RefreshCw, Search, Loader2, Copy, Send, ExternalLink, ClipboardList } from 'lucide-react';
import { UmrahFulfilmentDrawer } from './UmrahFulfilmentDrawer';
import { PartnerSyncCard } from './PartnerSyncCard';
import { EtgCertificationCard } from './EtgCertificationCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `£${Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
const day = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const STATUS = {
  PAID: { label: 'Paid in full', cls: 'bg-emerald-100 text-emerald-700' },
  DEPOSIT_PAID: { label: 'Deposit paid', cls: 'bg-amber-100 text-amber-700' },
  PENDING: { label: 'Awaiting payment', cls: 'bg-slate-200 text-slate-600' },
  CANCELLED: { label: 'Cancelled', cls: 'bg-red-100 text-red-700' },
  ISSUED: { label: 'Issued', cls: 'bg-emerald-600 text-white' },
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border bg-white p-3" data-testid={testId}><div className="text-xs text-slate-500">{label}</div><div className="text-xl font-bold text-slate-900">{value}</div></div>
);

const BalanceCell = ({ b }) => {
  if (b.payment_status === 'PAID') return <span className="text-emerald-700 font-semibold">£0</span>;
  if (b.payment_status !== 'DEPOSIT_PAID') return <span className="text-slate-400">—</span>;
  const overdue = b.balance_due_date && new Date(b.balance_due_date) < new Date(new Date().toDateString());
  return <div><div className="font-semibold text-slate-900">{money(b.balance_due)}</div><div className={`text-xs ${overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>due {day(b.balance_due_date)}{overdue ? ' · overdue' : ''}</div></div>;
};

export const UmrahBookingsPanel = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [sending, setSending] = useState(null);
  const [fulfil, setFulfil] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status !== 'all') params.status = status;
      const { data } = await axios.get(`${API_URL}/api/admin/umrah/bookings`, { params, withCredentials: true });
      setRows(data.bookings || []); setStats(data.stats || {});
    } catch (e) { toast.error(e.response?.status === 401 || e.response?.status === 403 ? 'Admin login required' : 'Failed to load Umrah bookings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyLink = async (b) => {
    if (!b.balance_url) return toast.error('No balance link yet — use Resend link to generate one');
    try { await navigator.clipboard.writeText(b.balance_url); toast.success('Pay-balance link copied'); } catch { toast.error('Could not copy'); }
  };
  const resend = async (b) => {
    setSending(b.booking_id);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/umrah/bookings/${b.booking_id}/resend-balance-link`, { origin_url: window.location.origin }, { withCredentials: true });
      toast.success(`Balance link emailed to ${data.to}${data.cc ? ` (cc ${data.cc})` : ''}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send link'); }
    finally { setSending(null); }
  };

  return (
    <div className="space-y-6">
    <PartnerSyncCard />
    <EtgCertificationCard />
    <Card data-testid="admin-umrah-bookings-panel">
      <CardHeader className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg"><Landmark className="h-5 w-5 text-[#FF6600]" />Umrah Package Bookings</CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="umrah-bookings-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Total bookings" value={stats.total ?? '—'} testId="umrah-stat-total" />
          <Stat label="Awaiting payment" value={stats.pending ?? '—'} testId="umrah-stat-pending" />
          <Stat label="Deposit paid" value={stats.deposit_paid ?? '—'} testId="umrah-stat-deposit" />
          <Stat label="Paid in full" value={stats.paid ?? '—'} testId="umrah-stat-paid" />
          <Stat label="Outstanding balances" value={money(stats.outstanding_balance)} testId="umrah-stat-outstanding" />
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Search ref, name, email or phone" className="pl-9" data-testid="umrah-bookings-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[190px]" data-testid="umrah-bookings-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="PENDING_PAYMENT">Awaiting payment</SelectItem>
              <SelectItem value="DEPOSIT_PAID">Deposit paid</SelectItem>
              <SelectItem value="PAID">Paid in full</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} data-testid="umrah-bookings-search-btn">Search</Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? <div className="py-10 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : rows.length === 0 ? (
          <div className="py-10 text-center text-slate-500" data-testid="umrah-bookings-empty">No Umrah package bookings found.</div>
        ) : (
          <Table data-testid="umrah-bookings-table">
            <TableHeader><TableRow>
              <TableHead>Ref / booked</TableHead><TableHead>Customer</TableHead><TableHead>Package</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => {
                const st = b.status === 'CANCELLED' ? STATUS.CANCELLED : b.fulfilment_issued ? STATUS.ISSUED : (STATUS[b.payment_status] || STATUS.PENDING);
                return (
                  <TableRow key={b.booking_id} data-testid={`umrah-booking-row-${b.booking_id}`}>
                    <TableCell><div className="font-mono font-bold">{b.booking_id}</div><div className="text-xs text-slate-500">{when(b.created_at)}</div></TableCell>
                    <TableCell><div className="font-medium">{b.lead_passenger || '—'}</div><div className="text-xs text-slate-500 break-all">{b.contact_email}</div><div className="text-xs text-slate-500">{b.contact_phone}</div></TableCell>
                    <TableCell><div className="font-medium">{b.category}</div><div className="text-xs text-slate-500">{day(b.flight?.departure_date)} → {day(b.flight?.return_date)} · {b.passengers?.total} pax</div><div className="text-xs text-slate-500">{(b.hotels || []).map((h) => h.name).join(' · ')}</div></TableCell>
                    <TableCell className="text-right font-semibold">{money(b.total)}</TableCell>
                    <TableCell className="text-right">{money(b.paid)}</TableCell>
                    <TableCell><BalanceCell b={b} /></TableCell>
                    <TableCell><Badge className={`${st.cls} border-0`} data-testid={`umrah-booking-status-${b.booking_id}`}>{st.label}</Badge>{b.payment_plan === 'DEPOSIT' && b.payment_status === 'DEPOSIT_PAID' && b.balance_link_sent_at && <div className="text-[11px] text-slate-500 mt-1">link sent {when(b.balance_link_sent_at)}</div>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setFulfil(b.booking_id)} title="Fulfilment & documents" data-testid={`umrah-booking-fulfil-${b.booking_id}`}><ClipboardList className="h-4 w-4 mr-1" />Fulfil</Button>
                        {b.can_pay && <>
                          <Button size="sm" variant="outline" onClick={() => copyLink(b)} title="Copy pay-balance link" data-testid={`umrah-booking-copy-link-${b.booking_id}`}><Copy className="h-4 w-4" /></Button>
                          <Button size="sm" onClick={() => resend(b)} disabled={sending === b.booking_id} className="bg-[#FF6600] hover:bg-[#e65c00] text-white" data-testid={`umrah-booking-resend-link-${b.booking_id}`}>{sending === b.booking_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}{sending === b.booking_id ? '' : 'Resend link'}</Button>
                        </>}
                        {b.balance_url && <Button size="sm" variant="ghost" asChild title="Open pay-balance page" data-testid={`umrah-booking-open-link-${b.booking_id}`}><a href={b.balance_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    {fulfil && <UmrahFulfilmentDrawer bookingId={fulfil} onClose={() => setFulfil(null)} onChanged={load} />}
    </div>
  );
};
